import React, { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PROJECT_CONFIG from "../config/projectConfig";
import { useAuth } from "../hooks/useAuth";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const menuId = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveDropdown(null);
        if (isMenuOpen) {
          setIsMenuOpen(false);
          mobileMenuButtonRef.current?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  const navigationItems = [
    {
      label: `About ${PROJECT_CONFIG.name}`,
      dropdown: true,
      items: [
        { label: "Overview", path: "/about" },
        {
          label: `${PROJECT_CONFIG.name} Process`,
          path: "/process",
        },
        {
          label: `${PROJECT_CONFIG.name} Rules & Procedures`,
          path: "/rules-procedures",
        },
      ],
    },
    {
      label: "Panels",
      path: "/panels",
    },
    {
      label: "Resources",
      dropdown: true,
      items: [
        { label: "Technical Resources", path: "/resources" },
        { label: "FAQs", path: "/faqs" },
        { label: "Contact", path: "/contact" },
      ],
    },
  ];

  if (isAuthenticated) {
    navigationItems.push({
      label: "Dashboard",
      path: "/dashboard",
    });
  }

  if (user?.role === "PLATFORM_ADMIN") {
    navigationItems.push({
      label: "Admin",
      dropdown: true,
      items: [
        { label: "Create Panel", path: "/panel-creation" },
        { label: "Manage Panels", path: "/panels-management" },
      ],
    });
  }

  const linkFocus =
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-custom-blue rounded";

  return (
    <nav className="bg-custom-blue shadow-lg sticky top-0 z-50" aria-label="Main">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className={`flex items-center ${linkFocus}`}>
              <div className="flex flex-col">
                <span className="text-white text-xl font-bold">
                  {PROJECT_CONFIG.name}
                </span>
                <span className="text-sky-200 text-xs">
                  {PROJECT_CONFIG.fullName}
                </span>
              </div>
            </Link>
          </div>

          <div
            className="hidden md:flex items-center space-x-8"
            ref={dropdownRef}
          >
            {navigationItems.map((item, index) => {
              const dropdownId = `${menuId}-dropdown-${index}`;
              return (
                <div key={index} className="relative">
                  {item.dropdown ? (
                    <>
                      <button
                        type="button"
                        id={`${dropdownId}-button`}
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === index}
                        aria-controls={dropdownId}
                        onClick={() =>
                          setActiveDropdown(
                            activeDropdown === index ? null : index
                          )
                        }
                        className={`text-white hover:text-sky-200 px-3 py-2 text-sm font-medium flex items-center transition-colors ${linkFocus} ${
                          item.items.some((subItem) => isActive(subItem.path))
                            ? "text-sky-200"
                            : ""
                        }`}
                      >
                        {item.label}
                        <svg
                          className="ml-1 w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {activeDropdown === index && (
                        <div
                          id={dropdownId}
                          aria-labelledby={`${dropdownId}-button`}
                          className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 motion-safe:animate-fadeIn"
                        >
                          <div className="py-1">
                            {item.items.map((subItem, subIndex) => (
                              <Link
                                key={subIndex}
                                to={subItem.path}
                                className={`block px-4 py-2 text-sm focus:outline-none focus-visible:bg-gray-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 ${
                                  isActive(subItem.path)
                                    ? "bg-custom-blue text-white hover:bg-custom-blue"
                                    : "text-gray-900 hover:bg-gray-100"
                                }`}
                                onClick={() => setActiveDropdown(null)}
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      aria-current={isActive(item.path) ? "page" : undefined}
                      className={`text-white hover:text-sky-200 px-3 py-2 text-sm font-medium transition-colors ${linkFocus} ${
                        isActive(item.path) ? "text-sky-200" : ""
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              );
            })}

            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <span className="text-white text-sm">{user?.name}</span>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={`bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors ${linkFocus}`}
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`text-white hover:text-sky-200 px-3 py-2 text-sm font-medium transition-colors ${linkFocus}`}
                  >
                    Login
                  </Link>
                  <Link
                    to="/cadre-application"
                    className={`bg-white hover:bg-srp-foam text-custom-blue px-4 py-2 rounded-md text-sm font-medium transition-colors ${linkFocus}`}
                  >
                    Apply as Cadre
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="md:hidden flex items-center">
            <button
              ref={mobileMenuButtonRef}
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`text-white hover:text-sky-200 p-2 ${linkFocus}`}
              aria-expanded={isMenuOpen}
              aria-controls={`${menuId}-mobile`}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div
          id={`${menuId}-mobile`}
          className="md:hidden bg-custom-blue border-t border-sky-300"
        >
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigationItems.map((item, index) => {
              const dropdownId = `${menuId}-mobile-dropdown-${index}`;
              return (
                <div key={index}>
                  {item.dropdown ? (
                    <>
                      <button
                        type="button"
                        aria-expanded={activeDropdown === index}
                        aria-controls={dropdownId}
                        onClick={() =>
                          setActiveDropdown(
                            activeDropdown === index ? null : index
                          )
                        }
                        className={`w-full text-left text-white hover:bg-white/10 px-3 py-2 text-base font-medium flex items-center justify-between ${linkFocus}`}
                      >
                        {item.label}
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={
                              activeDropdown === index
                                ? "M5 15l7-7 7 7"
                                : "M19 9l-7 7-7-7"
                            }
                          />
                        </svg>
                      </button>
                      {activeDropdown === index && (
                        <div id={dropdownId} className="pl-4">
                          {item.items.map((subItem, subIndex) => (
                            <Link
                              key={subIndex}
                              to={subItem.path}
                              aria-current={
                                isActive(subItem.path) ? "page" : undefined
                              }
                              className={`block text-white hover:bg-white/10 px-3 py-2 text-sm ${linkFocus} ${
                                isActive(subItem.path) ? "bg-white/10" : ""
                              }`}
                              onClick={() => {
                                setIsMenuOpen(false);
                                setActiveDropdown(null);
                              }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      aria-current={isActive(item.path) ? "page" : undefined}
                      className={`block text-white hover:bg-white/10 px-3 py-2 text-base font-medium ${linkFocus} ${
                        isActive(item.path) ? "bg-white/10" : ""
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              );
            })}

            <div className="border-t border-sky-300 pt-4">
              {isAuthenticated ? (
                <>
                  <div className="px-3 py-2 text-white text-sm">
                    Logged in as: {user?.name}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className={`w-full text-left bg-red-700 hover:bg-red-800 text-white px-3 py-2 text-base font-medium ${linkFocus}`}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`block text-white hover:bg-white/10 px-3 py-2 text-base font-medium ${linkFocus}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/cadre-application"
                    className={`block bg-white hover:bg-srp-foam text-custom-blue px-3 py-2 text-base font-medium ${linkFocus}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Apply as Cadre
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
